import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Typography,
    Box,
    Paper,
} from "@mui/material";
import { useState } from "react";

import {
    DocumentInfo,
    DocumentSection,
} from "../../../../../../../models/documents";
import { TReadBudgetsResponse } from "../../../../../../../services/budget/read-budgets";
import { TReadBudgetsLevelsDetailsByIdResponse } from "../../../../../../../services/budget/read-budgets-levels-detailed-by-id";
import ActivityProfileTable from "./tables/ActivityProfileTable";
import ScopeTable from "./tables/ScopeTable";
import DocumentDownload from "./templates/DocumentDownload";

interface IDocumentPreviewProps {
    sections: DocumentSection[];
    documentInfo: DocumentInfo & Partial<{ date: string }>;
    budgetData: TReadBudgetsResponse;
    tableScopeData: TReadBudgetsLevelsDetailsByIdResponse;
}

export default function DocumentPreview({
    sections,
    documentInfo,
    budgetData,
    tableScopeData,
}: IDocumentPreviewProps) {
    const [open, setOpen] = useState(false);

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const formattedDate = documentInfo.date
        ? new Date(documentInfo.date).toLocaleDateString("pt-BR")
        : new Date().toLocaleDateString("pt-BR");

    const profiles = budgetData.BudgetProfiles;

    const renderSectionContent = (section: DocumentSection) => {
        switch (section.type) {
            case "tabela_de_escopo":
                return (
                    <ScopeTable
                        budgetData={budgetData}
                        items={tableScopeData}
                    />
                );
            case "tabela_de_perfis_de_atividade":
                return <ActivityProfileTable items={profiles} />;
            default:
                return (
                    <Typography sx={{ whiteSpace: "pre-wrap" }}>
                        {section.content}
                    </Typography>
                );
        }
    };

    return (
        <>
            <Button variant="outlined" onClick={handleOpen}>
                Visualizar Documento
            </Button>

            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: { maxHeight: "90vh" },
                }}
            >
                <DialogTitle>Visualização do Documento</DialogTitle>

                <DialogContent>
                    <Paper sx={{ p: 4, mt: 2, bgcolor: "white", mb: 2 }}>
                        {/* Cabeçalho do documento */}
                        <Box
                            sx={{
                                borderBottom: "1px solid",
                                borderColor: "divider",
                                pb: 2,
                                mb: 3,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <Box>
                                    <Typography variant="h5">
                                        {documentInfo.document_title ||
                                            "DPP - Documento de Proposição de Projeto"}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {documentInfo.enterprise &&
                                        documentInfo.project
                                            ? `${documentInfo.enterprise} | ${documentInfo.project}`
                                            : "Empresa | Projeto"}
                                    </Typography>
                                </Box>
                                <Box sx={{ textAlign: "right" }}>
                                    <Typography variant="body2">
                                        Data: {formattedDate}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* Conteúdo do documento */}
                        {sections.length === 0 ? (
                            <Typography color="text.secondary">
                                Nenhuma seção adicionada ao documento.
                            </Typography>
                        ) : (
                            sections
                                .sort((a, b) => a.position - b.position)
                                .map((section) => (
                                    <Box key={section.id_e} sx={{ mb: 3 }}>
                                        <Typography variant="h6" gutterBottom>
                                            {section.position}.{" "}
                                            {section.session_title}
                                        </Typography>
                                        {renderSectionContent(section)}
                                    </Box>
                                ))
                        )}

                        {/* Rodapé do documento */}
                        <Box
                            sx={{
                                mt: 6,
                                pt: 2,
                                borderTop: "1px solid",
                                borderColor: "divider",
                            }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Este documento foi gerado em {formattedDate}.
                            </Typography>
                        </Box>
                    </Paper>

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 2,
                            mt: 2,
                        }}
                    >
                        <DocumentDownload
                            sections={sections}
                            documentInfo={documentInfo}
                            profiles={profiles}
                            budgetData={budgetData}
                            tableScopeData={tableScopeData}
                        />
                        <Button
                            variant="contained"
                            onClick={() => window.print()}
                        >
                            Imprimir
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
}
